import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { providerApi } from '../../services/api';
import { formatPrice } from '../../utils/currencyUtils';
import {
  formatTimeLeft,
  isStaleRefundError,
  isUnknownRefundOutcome,
  storeActionError,
  timeLeft,
} from '../../utils/storeFulfilment';

const HISTORY_STATUSES = ['REJECTED', 'REFUNDED', 'CANCELLED'];
const NOTE_MAX = 1000;

const STATUS_COLORS = {
  REQUESTED: 'warning',
  APPROVED: 'info',
  ESCALATED: 'error',
  REFUNDED: 'success',
  REJECTED: 'default',
  CANCELLED: 'default',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '');

/** Re-renders every minute so the respond-by countdown stays honest while the page is open. */
function useNow(intervalMs = 60000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Parses a money input: positive, at most two decimals. Returns a number or null. */
export function parseAmount(raw) {
  const text = String(raw ?? '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const n = Number(text);
  return n > 0 ? n : null;
}

const isGatewayRefusal = (err) => err?.response?.status === 502 && err?.response?.data?.code === 'REFUND_REJECTED';

/**
 * Approve (full or partial) a return. The amount is pre-filled with everything still refundable;
 * asking for less requires a reason, mirroring the server rule (PARTIAL_REFUND_NEEDS_REASON).
 */
function ApproveDialog({ item, onClose, onDone, onStale, onRefresh }) {
  const { t } = useTranslation();
  const { order } = item;
  const refundable = Number(order.refundableAmount);
  const [amount, setAmount] = useState(String(order.refundableAmount));
  const [restock, setRestock] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // After an unknown outcome the request is already APPROVED server-side and a repeat settles the
  // ORIGINAL decision (a new amount / restock / note would be ignored), so the inputs lock and the
  // retry visibly re-sends the same values.
  const [locked, setLocked] = useState(false);

  const parsed = parseAmount(amount);
  const amountError = parsed === null
    ? t('returns.amountInvalid')
    : parsed > refundable
      ? t('returns.amountTooHigh', { max: formatPrice(refundable, order.currencyCode) })
      : '';
  const isPartial = parsed !== null && parsed < refundable;
  const noteMissing = isPartial && note.trim() === '';
  const canSubmit = !busy && !amountError && !noteMissing && note.length <= NOTE_MAX;

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await providerApi.approveStoreReturn(order.orderId, item.returnRequest.id, {
        amount: isPartial ? parsed : null, // null = everything that remains
        restock,
        note,
      });
      onDone(t('returns.approvedOk'));
    } catch (err) {
      setLocked(isUnknownRefundOutcome(err));
      const message = storeActionError(err, t, { refund: true });
      if (isStaleRefundError(err) && !isGatewayRefusal(err)) {
        // Another refund in flight / the amount no longer fits: the refundable balance this dialog
        // was built from is out of date, so close it (the queue shows the error and the fresh
        // balance) instead of leaving a stale maximum to submit against.
        if (onStale) {
          onStale(message);
          return;
        }
      }
      setError(message);
      // The gateway refused and re-opened the request: nothing else changed, so the dialog stays
      // for a corrected attempt while the queue reloads.
      if (isStaleRefundError(err) && onRefresh) onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('returns.approveTitle', { id: item.returnRequest.id })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t('returns.approveHelp', { max: formatPrice(refundable, order.currencyCode) })}
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          margin="dense"
          label={t('returns.refundAmount')}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={Boolean(amountError)}
          helperText={amountError || ' '}
          inputProps={{ inputMode: 'decimal', dir: 'ltr' }}
          disabled={busy || locked}
        />
        <FormControlLabel
          control={<Switch checked={restock} onChange={(e) => setRestock(e.target.checked)} disabled={busy || locked} />}
          label={t('returns.restock')}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          margin="dense"
          label={isPartial ? t('returns.partialReasonLabel') : t('returns.noteOptional')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          required={isPartial}
          error={noteMissing || note.length > NOTE_MAX}
          helperText={noteMissing ? t('returns.partialNeedsReason') : ' '}
          inputProps={{ maxLength: NOTE_MAX + 1 }}
          disabled={busy || locked}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={submit}
          disabled={!canSubmit}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {t('returns.approveConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Reject a return; the buyer sees the note, so it is required. */
function RejectDialog({ item, onClose, onDone, onRefresh }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = !busy && note.trim() !== '' && note.length <= NOTE_MAX;

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await providerApi.rejectStoreReturn(item.order.orderId, item.returnRequest.id, { note: note.trim() });
      onDone(t('returns.rejectedOk'));
    } catch (err) {
      setError(storeActionError(err, t));
      // A 409 means the request was changed at the same time by someone else: reload the queue so
      // "refresh and try again" shows the real state.
      if (err?.response?.status === 409 && onRefresh) onRefresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('returns.rejectTitle', { id: item.returnRequest.id })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('returns.rejectHelp')}</DialogContentText>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          multiline
          minRows={3}
          margin="dense"
          required
          label={t('returns.rejectNote')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          error={note.length > NOTE_MAX}
          inputProps={{ maxLength: NOTE_MAX + 1 }}
          disabled={busy}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          onClick={submit}
          disabled={!canSubmit}
          startIcon={busy ? <CircularProgress size={16} /> : null}
        >
          {t('returns.rejectConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReturnCard({ item, now, busy, onApprove, onReject, onRetry }) {
  const { t } = useTranslation();
  const { returnRequest: r, order } = item;
  const left = r.status === 'REQUESTED' ? timeLeft(r.respondBy, now) : null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`return-card-${r.id}`}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            {t('returns.cardTitle', { id: r.id, orderId: order.orderId })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('returns.buyer')}: {order.buyerName || t('orders.na')} · {t('returns.requestedAt')}: {fmtDate(r.requestedAt)}
          </Typography>
        </Box>
        <Chip label={t(`returns.status${r.status}`)} color={STATUS_COLORS[r.status] || 'default'} size="small" />
      </Box>

      {left && !left.expired && (
        <Alert severity="info" sx={{ mt: 1.5 }} data-testid={`return-deadline-${r.id}`}>
          {t('returns.respondWithin', { time: formatTimeLeft(left, t), date: fmtDate(r.respondBy) })}
        </Alert>
      )}
      {left && left.expired && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid={`return-expired-${r.id}`}>
          {t('returns.respondExpired')}
        </Alert>
      )}
      {r.status === 'APPROVED' && (
        <Alert severity="info" sx={{ mt: 1.5 }}>{t('returns.refundPending')}</Alert>
      )}
      {r.status === 'ESCALATED' && (
        <Alert severity="warning" sx={{ mt: 1.5 }} data-testid={`return-escalated-${r.id}`}>
          {r.escalationReason === 'BUYER_DISPUTE'
            ? t('returns.escalatedDispute')
            : t('returns.escalatedNoResponse')}
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="body2">
        <strong>{t('returns.reason')}:</strong> {t(`returns.reason${r.reason}`, { defaultValue: r.reason })}
      </Typography>
      {r.buyerNote && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>{t('returns.buyerNote')}:</strong> {r.buyerNote}
        </Typography>
      )}
      {r.disputeNote && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>{t('returns.disputeNote')}:</strong> {r.disputeNote}
        </Typography>
      )}

      <Box sx={{ mt: 1.5 }}>
        {(order.items || []).map((it, idx) => (
          <Typography variant="body2" key={`${it.productId ?? 'x'}-${idx}`}>
            {it.quantity} × {it.name} — {formatPrice(it.totalPrice, order.currencyCode)}
          </Typography>
        ))}
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('returns.orderTotal')}: {formatPrice(order.total, order.currencyCode)}
          {Number(order.refundedAmount) > 0 && ` · ${t('returns.alreadyRefunded')}: ${formatPrice(order.refundedAmount, order.currencyCode)}`}
        </Typography>
        {order.deliveredAt && (
          <Typography variant="body2" color="text.secondary">
            {t('returns.deliveredAt')}: {fmtDate(order.deliveredAt)}
          </Typography>
        )}
      </Box>

      {(r.storeNote || r.adminNote || r.refundAmount != null) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          {r.refundAmount != null && (
            <Typography variant="body2">
              <strong>{t('returns.refundedAmount')}:</strong> {formatPrice(r.refundAmount, order.currencyCode)}
              {r.restock ? ` · ${t('returns.restocked')}` : ''}
            </Typography>
          )}
          {r.storeNote && (
            <Typography variant="body2"><strong>{t('returns.yourNote')}:</strong> {r.storeNote}</Typography>
          )}
          {r.adminNote && (
            <Typography variant="body2"><strong>{t('returns.adminNote')}:</strong> {r.adminNote}</Typography>
          )}
        </>
      )}

      {r.status === 'REQUESTED' && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" color="success" size="small" disabled={busy} onClick={() => onApprove(item)}>
            {t('returns.approve')}
          </Button>
          <Button variant="outlined" color="error" size="small" disabled={busy} onClick={() => onReject(item)}>
            {t('returns.reject')}
          </Button>
        </Box>
      )}
      {r.status === 'APPROVED' && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disabled={busy} onClick={() => onRetry(item)}>
            {t('returns.retryRefund')}
          </Button>
        </Box>
      )}
    </Paper>
  );
}

/**
 * The store's Returns queue: open requests with their respond-by deadline, approve (full or
 * partial, with restock) / reject, and a history view. `onChanged` lets the dashboard refresh the
 * nav badge after a decision.
 */
const StoreReturns = ({ onChanged }) => {
  const { t } = useTranslation();
  const now = useNow();
  const [view, setView] = useState('open');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await providerApi.getStoreReturns(
        view === 'history' ? { statuses: HISTORY_STATUSES } : {},
      );
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load store returns:', err);
      setItems([]);
      setLoadError(t('returns.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [view, t]);

  useEffect(() => { load(); }, [load]);

  // Reload the queue and the nav badge after something moved the request without a clean decision.
  const refresh = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const handleDecided = async (message) => {
    setApproving(null);
    setRejecting(null);
    setActionError('');
    setSuccess(message);
    await refresh();
  };

  // Finishes a refund left APPROVED by an unknown gateway outcome: the approve call is idempotent.
  const handleRetry = async (item) => {
    setBusyId(item.returnRequest.id);
    setActionError('');
    setSuccess('');
    try {
      await providerApi.approveStoreReturn(item.order.orderId, item.returnRequest.id, {});
      await handleDecided(t('returns.approvedOk'));
    } catch (err) {
      setActionError(storeActionError(err, t, { refund: true }));
      if (isStaleRefundError(err)) await refresh();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>{t('returns.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('returns.subtitle')}</Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(e, next) => { if (next) { setView(next); setSuccess(''); setActionError(''); } }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="open">{t('returns.tabOpen')}</ToggleButton>
          <ToggleButton value="history">{t('returns.tabHistory')}</ToggleButton>
        </ToggleButtonGroup>

        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>{t('common.retry')}</Button>}>
            {loadError}
          </Alert>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {view === 'history' ? t('returns.emptyHistory') : t('returns.emptyOpen')}
            </Typography>
          </Box>
        ) : (
          items.map((item) => (
            <ReturnCard
              key={item.returnRequest.id}
              item={item}
              now={now}
              busy={busyId === item.returnRequest.id}
              onApprove={(i) => { setSuccess(''); setApproving(i); }}
              onReject={(i) => { setSuccess(''); setRejecting(i); }}
              onRetry={handleRetry}
            />
          ))
        )}

        {approving && (
          <ApproveDialog
            item={approving}
            // A failed approve can still have moved the request (502 reopens it, an unknown
            // outcome leaves it APPROVED), so closing always reloads the queue and the nav badge.
            onClose={() => { setApproving(null); refresh(); }}
            onDone={handleDecided}
            onStale={(message) => {
              setApproving(null);
              setSuccess('');
              setActionError(message);
              refresh();
            }}
            onRefresh={refresh}
          />
        )}
        {rejecting && (
          <RejectDialog
            item={rejecting}
            onClose={() => setRejecting(null)}
            onDone={handleDecided}
            onRefresh={refresh}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default StoreReturns;
